import { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

const AUTOSAVE_DELAY_MS = 3000;

export const useAutosaveDraft = ({
  attachments,
  canSaveDraft,
  getFormValues,
  lastSavedSnapshotRef,
  saveDraft
}) => {
  const canSaveDraftRef = useRef(canSaveDraft);
  const saveDraftRef = useRef(saveDraft);
  const attachmentsRef = useRef(attachments);
  const initialSnapshotRef = useRef({
    ...getFormValues(),
    attachments
  });

  canSaveDraftRef.current = canSaveDraft;
  saveDraftRef.current = saveDraft;
  attachmentsRef.current = attachments;

  const debouncedSave = useRef(
    debounce((formValues, currentAttachments) => {
      if (!canSaveDraftRef.current) return Promise.resolve(null);

      return saveDraftRef.current(
        { ...formValues, attachments: currentAttachments },
        { silent: true }
      );
    }, AUTOSAVE_DELAY_MS)
  ).current;

  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  useEffect(() => {
    if (canSaveDraft) return;

    debouncedSave.cancel();
  }, [canSaveDraft, debouncedSave]);

  const triggerAutosave = useCallback(
    (formValues, currentAttachments) => {
      if (!canSaveDraftRef.current) return;
      debouncedSave(formValues, currentAttachments);
    },
    [debouncedSave]
  );

  const hasUnsavedChanges = useCallback(() => {
    const baselineSnapshot = lastSavedSnapshotRef.current || initialSnapshotRef.current;

    return !isEqual(baselineSnapshot, {
      ...getFormValues(),
      attachments: attachmentsRef.current
    });
  }, [lastSavedSnapshotRef, getFormValues]);

  const flushAutosave = useCallback(() => {
    return debouncedSave.flush();
  }, [debouncedSave]);

  const cancelAutosave = useCallback(() => {
    debouncedSave.cancel();
  }, [debouncedSave]);

  return { cancelAutosave, flushAutosave, hasUnsavedChanges, triggerAutosave };
};
